import React from 'react';

import { Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Form } from '@opendoor/atoms/form';
import { Text } from '@opendoor/atoms/text';
import { useForm } from '@tanstack/react-form';
import { useSelector } from '@tanstack/react-store';
import { z } from 'zod';

import { LinkButton } from '../../components/LinkButton';

const emailSchema = z.object({
  email: z.email({ error: 'Please enter a valid email address' }),
});

const loginSchema = z.object({
  password: z.string().min(1, { error: 'Please enter your password' }),
});

const registerSchema = z.object({
  name: z.string().trim().min(1, { error: 'Please enter your name' }),
  password: z.string().min(1, { error: 'Please create a password' }),
  // Optional, but a number that is filled in has to be complete. `Form.InputPhone`
  // stores digits only, so a US number is 10 of them.
  phone: z.string().refine((value) => value === '' || value.replace(/\D/g, '').length >= 10, {
    error: 'Please enter a valid phone number',
  }),
});

interface AccountFormProps {
  email: string;
  error: string;
  isNewHuman: boolean;
  hasGeneratedPassword: boolean;
  onLogin: (email: string, password: string) => void;
  onRegister: (email: string, name: string, password: string, phone: string) => void;
  onHumanStatusRequest: (email: string) => void;
}

const AccountForm: React.FC<AccountFormProps> = ({
  email: initialEmail,
  error,
  isNewHuman,
  hasGeneratedPassword,
  onLogin,
  onRegister,
  onHumanStatusRequest,
}) => {
  const [hasSubmittedEmail, setHasSubmittedEmail] = React.useState(false);

  const emailForm = useForm({
    defaultValues: { email: initialEmail },
    validators: { onChange: emailSchema },
    onSubmit: ({ value }) => {
      onHumanStatusRequest(value.email);
      setHasSubmittedEmail(true);
    },
  });

  // The later steps submit against the address the human-status lookup ran on,
  // and the heading echoes it back, so both read the live field value.
  const email = useSelector(emailForm.store, (state) => state.values.email);

  const loginForm = useForm({
    defaultValues: { password: '' },
    validators: { onChange: loginSchema },
    onSubmit: ({ value }) => onLogin(email, value.password),
  });

  const registerForm = useForm({
    defaultValues: { name: '', password: '', phone: '' },
    validators: { onChange: registerSchema },
    onSubmit: ({ value }) => onRegister(email, value.name, value.password, value.phone),
  });

  const returnToEmailStep = () => setHasSubmittedEmail(false);

  if (hasGeneratedPassword) {
    return (
      <Col key="check-email" className="w-full max-w-100 gap-7">
        <Text as="h2" className="text-header-sm text-center">
          Check your email
        </Text>
        <Text as="p" className="text-body-md text-center">
          We sent a password setup link to{' '}
          <Text as="span" className="text-body-md font-medium">
            {email}
          </Text>
          . Please check your inbox and follow the instructions to set your password.
        </Text>
      </Col>
    );
  }

  if (!hasSubmittedEmail) {
    return (
      // `noValidate` so the browser's own email validation can't swallow the
      // submit event before the schema's message gets a chance to render.
      <Form.Root
        key="email-step"
        form={emailForm}
        noValidate
        className="flex w-full max-w-100 flex-col gap-7"
      >
        <Text as="h2" className="text-header-sm text-center">
          Enter your email
        </Text>
        <Form.Input
          name="email"
          label="Email"
          type="email"
          placeholder="you@example.com"
          className="w-full"
          autoFocus
        />
        {error ? (
          <Text as="p" className="text-body-sm">
            {error}
          </Text>
        ) : null}
        <Button
          variant="primary"
          className="w-full"
          onClick={() => void emailForm.handleSubmit()}
          analyticsName="partnership-sign-up-email-submit"
          aria-label="Continue"
        >
          Continue
        </Button>
      </Form.Root>
    );
  }

  if (!isNewHuman) {
    return (
      <Form.Root key="login-step" form={loginForm} className="flex w-full max-w-100 flex-col gap-7">
        <Text as="h2" className="text-header-sm text-center">
          Welcome back
        </Text>
        <Text as="p" className="text-body-md text-od-content-secondary text-center">
          Sign in as {email}
        </Text>
        <Form.Input
          name="password"
          label="Password"
          type="password"
          placeholder="Enter your password"
          className="w-full"
          autoFocus
        />
        {error ? (
          <Text as="p" className="text-body-sm">
            {error}
          </Text>
        ) : null}
        <Button
          variant="primary"
          className="w-full"
          onClick={() => void loginForm.handleSubmit()}
          analyticsName="partnership-sign-up-login-submit"
          aria-label="Sign In"
        >
          Sign In
        </Button>
        <LinkButton
          variant="primary"
          onClick={returnToEmailStep}
          analyticsName="partnership-sign-up-back-to-email"
          aria-label="Use a different email"
          className="text-label-lg"
        >
          Use a different email
        </LinkButton>
      </Form.Root>
    );
  }

  return (
    <Form.Root
      key="register-step"
      form={registerForm}
      className="flex w-full max-w-100 flex-col gap-7"
    >
      <Text as="h2" className="text-header-sm text-center">
        Create your account
      </Text>
      <Text as="p" className="text-body-md text-od-content-secondary text-center">
        Signing up as {email}
      </Text>
      <Form.Input
        name="name"
        label="Full name"
        placeholder="Jane Smith"
        className="w-full"
        autoFocus
      />
      <Form.Input
        name="password"
        label="Password"
        type="password"
        placeholder="Create a password"
        className="w-full"
      />
      <Form.InputPhone
        name="phone"
        label="Phone number"
        placeholder="(555) 555-5555"
        className="w-full"
        required={false}
      />
      {error ? (
        <Text as="p" className="text-body-sm">
          {error}
        </Text>
      ) : null}
      <Button
        variant="primary"
        className="w-full"
        onClick={() => void registerForm.handleSubmit()}
        analyticsName="partnership-sign-up-register-submit"
        aria-label="Create Account"
      >
        Create Account
      </Button>
      <LinkButton
        onClick={returnToEmailStep}
        analyticsName="partnership-sign-up-back-to-email"
        aria-label="Use a different email"
        className="text-label-lg"
        variant="secondary"
      >
        Use a different email
      </LinkButton>
    </Form.Root>
  );
};

export { AccountForm };
